import { IconButton } from '@fluentui/react';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Text from 'src/components/text/Text';
import localeService from 'src/dataServices/LocaleService';
import { getLocalizedPath } from '../utilities/Localization/Utils';
import './ConfirmationModal.scss';

export interface IConfirmationModal {
  revalidationRequired: boolean;
  onConfirmation?: Function;
  onCancelConfirmation?: Function;
  showConfirmation: boolean;
  convertAccount?: boolean;
  isCommercial?: boolean;
  profilePageEnhancementsEnabled?: boolean;
}

const InfoSection = ({ children }: { children: React.ReactNode }) => (
  <div className="profile-info-section">
    <img alt="" className="banner-icon" src={`${process.env.REACT_APP_HUB_IMAGES_CDN_URL}/icons/info-icon.svg`} />
    <p className="banner-text">{children}</p>
  </div>
);

export default (props: IConfirmationModal) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.showConfirmation) return;

    const modal = dialogRef.current;
    if (!modal) return;

    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(modal.querySelectorAll<HTMLElement>(focusableSelectors));
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        props.onCancelConfirmation?.();
      }
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [props.showConfirmation]);

  const ConfirmationText = () => {
    if (props.convertAccount) {
      return (
        <>
          <h6 className="h6" id={'confirmation-modal-title'}>
            <Text id={'RegisterNonprofitAccount'} category={'ProfileV2'} />
          </h6>
          <p className="thanks-for-interest">
            <Text id={'ThankYouForInterest'} category={'ProfileV2'} />
          </p>
          <p className="register-questions">
            <Text id={'RegisterQuestions'} category={'ProfileV2'} />
          </p>
        </>
      );
    }

    if (props.revalidationRequired) {
      return (
        <>
          <h6 className="h6" id={'confirmation-modal-title'}>
            <Text id={'SubmitForApproval'} category={'ProfileV2'} />
          </h6>
          <p className="thanks-for-updating">
            {props.isCommercial ? (
              <Text id={'ThankYouForRegistering'} category={'ProfileV2'} />
            ) : (
              <Text id={'ThankYouForUpdating'} category={'ProfileV2'} />
            )}
          </p>
          <p>
            <Text id={'YouMadeChanges'} category={'ProfileV2'} />
          </p>
        {(props.isCommercial || !props.profilePageEnhancementsEnabled) && (
          <>
          <InfoSection>
            <Text id={'DiscountBody'} category={'ProfileV2'} />
          </InfoSection>
          <InfoSection>
            <Text id={'IfAddingEmail'} category={'ProfileV2'} />
          </InfoSection>
          </>
        )}
        </>
      );
    }

    return (
      <>
        <h6 className="h6" id={'confirmation-modal-title'}>
          <Text id={'ConfirmUpdate'} category={'ProfileV2'} />
        </h6>
        <p className="thanks-for-updating">
          <Text id={'ThankYouForUpdating'} category={'ProfileV2'} />
        </p>
        {(props.isCommercial || !props.profilePageEnhancementsEnabled) && (
        <>
        <InfoSection>
          <Text id={'Affirmation'} category={'ProfileV2'} />
        </InfoSection>
        <InfoSection>
          <Text id={'IfAddingEmail'} category={'ProfileV2'} />
        </InfoSection>
        </>
        )}
      </>
    );
  };

  return (
    <div className={props.showConfirmation ? 'profile-confirm-modal-backdrop' : 'display-none'}>
      <div
        className="profile-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        {!props.convertAccount && (
          <IconButton
            aria-label={localeService.getText('ProfileV2', 'Cancel')}
            title={localeService.getText('ProfileV2', 'Cancel')}
            className="x-icon-button"
            onClick={() => props.onCancelConfirmation?.()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onCancelConfirmation?.();
              }
            }}
          >
            <img alt="" src={`${process.env.REACT_APP_HUB_IMAGES_CDN_URL}/icons/x-icon.svg`} />
          </IconButton>
        )}
        <ConfirmationText />
        <div className="profile-submit-edit-buttons flex-end">
          {!props.convertAccount && (
            <button
              type="button"
              className="profile-cancel-confirm-button btn"
              onClick={() => props.onCancelConfirmation?.()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  props.onCancelConfirmation?.();
                }
              }}
            >
              {localeService.getText('ProfileV2', 'Cancel')}
            </button>
          )}
          {!props.convertAccount && (
            <button
              type="submit"
              className="profile-submit-button btn btn-primary"
              onClick={() => props.onConfirmation?.()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  props.onCancelConfirmation?.();
                }
              }}
            >
              {localeService.getText('ProfileV2', 'ConfirmAndSubmit')}
            </button>
          )}
          {props.convertAccount && (
            <div id="profile-submit-button">
              <Link className="primary-button" to={getLocalizedPath('/new/profile')}>
                {localeService.getText('ProfileV2', 'ContinueButton')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
